import './ListItem.scss';

import React, { useCallback } from 'react';
import withCx, { CxProps } from 'fe-core/util/withCx';
import Box from 'fe-design-base/atoms/Box';
import Icon, { IconName } from 'fe-design-base/atoms/Icon';
import Text from 'fe-design-base/atoms/Text';
import baseColors from 'fe-design-base/styles/colors';

import CircularInfinite from '../Loaders/CircularInfinite';

import { leftContentWidth } from './helpers';
import { Variant } from './List';

const DEFAULT_VARIANT_MARGIN = 16;

interface ListItemProps {
  primary: string;
  secondary?: React.ReactNode | string;
  iconName?: IconName;
  illustration?: React.ReactNode;
  loading?: boolean;
  index: number;
  variant: Variant;
  quiet?: boolean;
  isMedium: boolean;
  asHTML?: boolean;
  success?: boolean;
}

const ListItem = ({
  asHTML,
  cx,
  cxEl,
  primary,
  secondary,
  iconName,
  illustration,
  index,
  variant,
  quiet,
  isMedium,
  success,
  loading,
}: ListItemProps & CxProps) => {
  const primaryTextStyles = isMedium ? 'body' : 'bodySm';
  const secondaryTextStyles = isMedium ? 'bodySm' : 'captions';

  const marginAdjust = useCallback(() => {
    if (variant === 'icon') {
      return isMedium ? '4px' : '2px';
    }
    return undefined;
  }, [variant, isMedium]);

  const leftContent = {
    bullet: (
      <Text
        // Screen readers can ignore the bullet character
        // since we're already using semantic list elements in the markup
        ariaHidden
        className={cxEl('bullet')}
        variant={primaryTextStyles}
      >
        •
      </Text>
    ),
    check: <Icon size="small" color="accent500" iconName="Success" mt="4px" />,
    checklist: (
      <Icon
        size={isMedium ? 'medium' : 'small'}
        color={success ? 'success300' : 'mono400'}
        iconName="Complete"
        mt={isMedium ? '2.5px' : '4px'}
      />
    ),
    number: (
      <Box
        vcenter
        hcenter
        w={20}
        h={20}
        bradiusfull
        b={`solid 1px ${quiet ? baseColors.purple700 : 'transparent'}`}
        bgcolor={quiet ? 'mono0' : 'purple500'}
        mt={isMedium ? '2px' : '1px'}
      >
        <Text color={quiet ? 'mono900' : 'mono0'} variant="captionBold">
          {index + 1}
        </Text>
      </Box>
    ),
    icon: (
      <Box
        vcenter
        hcenter
        w={isMedium ? 32 : 26}
        h={isMedium ? 32 : 26}
        bradiusfull
        bgcolor="purple500"
      >
        <Icon
          size={isMedium ? 'small' : 'xsmall'}
          color="mono0"
          iconName={iconName || 'Success'}
        />
      </Box>
    ),
    illustration: (
      <Box w={isMedium ? 24 : 20} h={isMedium ? 24 : 20}>
        {illustration}
      </Box>
    ),
  };

  return (
    <>
      <Box component="li" className={cx()}>
        <Box alignItemsStart>
          <Box
            className={cxEl('left-content')}
            maxw={leftContentWidth(isMedium, variant)}
            minw={leftContentWidth(isMedium, variant)}
          >
            {loading ? (
              <Box
                mt={isMedium ? '5px' : '2px'}
                maxw={
                  leftContentWidth(isMedium, variant) - DEFAULT_VARIANT_MARGIN
                }
                minw={
                  leftContentWidth(isMedium, variant) - DEFAULT_VARIANT_MARGIN
                }
                ml={1}
                h={20}
              >
                <CircularInfinite size="small" variant="dark" />
              </Box>
            ) : (
              leftContent[variant]
            )}
          </Box>
          <Box column gap="6px" mt={marginAdjust()}>
            <Text variant={primaryTextStyles} color="mono900" asHTML={asHTML}>
              {primary}
            </Text>
            <Text variant={secondaryTextStyles} color="mono700" asHTML={asHTML}>
              {secondary}
            </Text>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default withCx<ListItemProps>('FDBListItem')(ListItem);
